import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useScrollLock } from "../../lib/useScrollLock";
import { useBackButtonClose } from "../../lib/useBackButtonClose";

/** True on >= sm (Tailwind 640px) viewports. Synchronous initial read avoids a flash. */
function useIsDesktop(): boolean {
  const query = "(min-width: 640px)";
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Header title; a "Done" button always sits opposite it. */
  title?: string;
  children: React.ReactNode;
  /** Extra classes on the panel (e.g. width overrides). */
  className?: string;
  /** Hide the title + "Done" header (for content that closes itself). */
  hideHeader?: boolean;
}

/**
 * Shared overlay surface: slides up from the bottom on mobile, and appears as a
 * centered, scale-in popup on desktop. Handles Escape, backdrop click, and
 * scroll lock. Used by the profile edit selectors (birthday, city, identity,
 * personality, …).
 */
export function Sheet({ open, onClose, title, children, className = "", hideHeader = false }: SheetProps) {
  useScrollLock(open);
  useBackButtonClose(open, onClose);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const panelMotion = isDesktop
    ? {
        initial: { opacity: 0, scale: 0.95 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 },
        transition: { type: "spring" as const, damping: 28, stiffness: 320 },
      }
    : {
        initial: { y: "100%" },
        animate: { y: 0 },
        exit: { y: "100%" },
        transition: { type: "spring" as const, damping: 30, stiffness: 300 },
      };

  return (
    <AnimatePresence>
      {open && (
        // The AnimatePresence direct child must be a motion element so it can
        // defer this subtree's unmount until the nested panel's exit finishes.
        // As a plain <div> the wrapper unmounted synchronously, cutting off the
        // subtle desktop scale/opacity exit (mobile's big y-slide masked it).
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4"
          onClick={(e) => {
            // Stop the backdrop click from bubbling to ancestor handlers (the
            // Sheet renders inline, not via a portal — e.g. a clickable card it
            // sits inside would otherwise navigate on dismiss).
            e.stopPropagation();
            onClose();
          }}
        >
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
          <motion.div
            {...panelMotion}
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full max-w-md bg-white dark:bg-neutral-900 shadow-2xl rounded-t-2xl sm:rounded-2xl p-5 pb-8 sm:pb-5 max-h-[85vh] overflow-y-auto ${className}`}
          >
            {!hideHeader && (
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{title}</h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-1.5 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Done
                </button>
              </div>
            )}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
